import { z } from "zod";

/**
 * RFC 7807 problem+json is the only error shape the API returns. The mobile app switches on `code`,
 * which is stable forever, and treats `title` and `detail` as a fallback for copy it does not have.
 * Never change the meaning of a code; add a new one instead.
 */

export const errorCodes = [
  // Auth and identity
  "UNAUTHENTICATED",
  "TOKEN_INVALID",
  "FORBIDDEN",
  "ROLE_REQUIRED",
  /** A branded app is for one business (mobile V2, slice 2.3): the call names another one. */
  "APP_PINNED",
  "USER_NOT_PROVISIONED",
  /** Development-only test sign-in. The route that raises it does not exist in production. */
  "INVALID_CREDENTIALS",
  /** The sign-in code was wrong, or it had already expired. */
  "OTP_INVALID",
  /** Too many wrong codes against one number. The code is burned and a new one must be asked for. */
  "OTP_TOO_MANY_ATTEMPTS",
  /** The code could not be delivered, usually because the number is not on WhatsApp. */
  "OTP_SEND_FAILED",
  /** A signed-in user tried to add a phone or an email that is on another account. No merge in V1. */
  "IDENTIFIER_IN_USE",
  /** Removing this method would leave the account with no verified way to sign in. */
  "LAST_SIGN_IN_METHOD",

  // Generic request problems
  "VALIDATION_FAILED",
  "NOT_FOUND",
  "CONFLICT",
  "RATE_LIMITED",
  "IDEMPOTENCY_KEY_REQUIRED",
  "IDEMPOTENCY_KEY_REUSED",
  "IDEMPOTENCY_IN_PROGRESS",
  "INTERNAL_ERROR",
  /** Control Center: a Desk route was called without the business to act as. */
  "ACT_AS_BUSINESS_REQUIRED",
  /**
   * Teamna subscription billing: the business did not pay, so Desk is read-only for the owner and
   * admins. Billing, the owner's own account and everything staff and members do still work.
   */
  "BILLING_READ_ONLY",

  // Membership and invites
  "INVITE_NOT_FOUND",
  "INVITE_EXPIRED",
  "INVITE_EXHAUSTED",
  "ALREADY_A_MEMBER",
  "MEMBERSHIP_NOT_ACTIVE",
  /** The business booked for a participant whose member left the business. */
  "MEMBER_HAS_LEFT",
  "PARTICIPANT_NOT_FOUND",
  "PARTICIPANT_HAS_BOOKINGS",
  /** Account deletion (slice 0.2) waits for the person: they own a business, have bookings to come, or share a family. */
  "ACCOUNT_DELETE_BLOCKED",
  /** The family said "no profile photo" for this participant, so nobody can add one. */
  "PHOTO_NOT_ALLOWED",

  // Messaging
  "CONVERSATION_NOT_FOUND",
  "ANNOUNCEMENT_READ_ONLY",
  "MESSAGE_RATE_LIMITED",

  // Scheduling and booking
  "SESSION_NOT_FOUND",
  "SESSION_FULL",
  "SESSION_CANCELLED",
  "SESSION_STARTED",
  "BOOKING_NOT_FOUND",
  "ALREADY_BOOKED",
  /** The waiting list of a session closed: 6 hours before the start, or 1 hour where late places come as offers (`waitlistCutoffHours`). */
  "WAITLIST_CLOSED",
  /** The time to accept a waitlist offer ended (B2): the place went to the next person. */
  "WAITLIST_OFFER_EXPIRED",
  /** A person joined the waiting list of a session that still has a free place: book it instead. */
  "SESSION_NOT_FULL",
  "WAITLIST_ENTRY_NOT_FOUND",
  /** A month booking line whose session the business moved after the member picked it. */
  "SESSION_CHANGED",
  /**
   * A batch booking where one or more lines failed. Nothing was booked. `meta.refused` lists each
   * line with its index, `sessionId`, `participantId`, `code` and `detail`.
   */
  "BOOKINGS_REFUSED",
  /** A timetable publish with problems to fix first (a coach in two places, places under bookings). */
  "TIMETABLE_PROBLEMS",
  /** A member tried to book a child into a session of another group. They book their own group only. */
  "OTHER_GROUP_SESSION",
  "BOOKING_NOT_CONFIRMED",
  "BOOK_TOO_SOON",
  "BOOK_TOO_FAR_AHEAD",
  "GROUP_MISMATCH",
  /** Attendance was marked on a session that is on a later day. */
  "ATTENDANCE_TOO_EARLY",
  /** A staff member who does not run the session tried to mark who came. */
  "NOT_YOUR_SESSION",
  /** The roster or a mark changed on another device after the attendance screen read it. */
  "ATTENDANCE_CHANGED",
  // Cover requests between staff (migration 0072)
  /** The session has an open cover request already. */
  "COVER_ALREADY_ASKED",
  /** Nobody else is on the team, so nobody can get the offer. */
  "COVER_NOBODY_TO_ASK",
  /** Another person took the cover first (or it waits for the owner's approval). */
  "COVER_TAKEN",
  /** The request is closed: taken back, expired, or the session changed. */
  "COVER_CLOSED",
  /** The offer did not go to this person, or the person asked for it. */
  "COVER_NOT_OFFERED",

  // Policy
  "POLICY_CUTOFF_PASSED",
  "RESCHEDULE_LIMIT_REACHED",
  "RESCHEDULE_NOT_ALLOWED",

  // Credits, packages, payments
  "INSUFFICIENT_CREDITS",
  "PACKAGE_EXPIRED",
  /** The package that could pay is paused on the day of the session (migration 0071). */
  "PACKAGE_PAUSED",
  "PACKAGE_NOT_FOUND",
  "PAYMENT_NOT_FOUND",
  "PAYMENT_NOT_REFUNDABLE",
  "PAYMENT_REQUEST_NOT_FOUND",
  "PAYMENT_REQUEST_NOT_PAYABLE",
  "PAYMENT_PROVIDER_ERROR",
  "WHATSAPP_UNAVAILABLE",
  "PAYOUT_ACCOUNT_MISSING",
  /** Teamna turned card payments off for all businesses (Control Center switch). They come later. */
  "CARD_PAYMENTS_COMING_SOON",
  /**
   * Teamna is early access only (Control Center switch `owner_sign_up` is off), and this account is
   * not on the allow list. The person sends the early access form on teamna.app.
   */
  "SIGN_UP_CLOSED",
  /**
   * The package is an intro offer (`newClientsOnly`), and this account bought a package here before
   * or attended a session here. The owner can sell it at the desk with `overrideNewClientsOnly`.
   */
  "NEW_CLIENTS_ONLY",
  // Monthly membership (M2, migration 0079). Rules: membership.ts.
  /** Every class of the membership's month is used. */
  "MEMBERSHIP_CAP_REACHED",
  /** The month of the session is not paid yet, so the membership cannot pay for it. */
  "MEMBERSHIP_MONTH_UNPAID",
  /** The account has a running membership here already. */
  "MEMBERSHIP_RUNNING",
  /** The account has no running membership here. */
  "MEMBERSHIP_NOT_FOUND",
  /** A membership month cannot be frozen in V1. */
  "MEMBERSHIP_NO_FREEZE",

  // AI Assistant (strategy repo teamna/strategy/ai-assistant-v1.md)
  /** Teamna staff did not turn AI Assistant on for this business (Control Center). */
  "ASSISTANT_OFF",
  /** The business used all its AI Assistant turns for today. */
  "ASSISTANT_DAILY_LIMIT",
  /** Control Center act-as cannot read or write the conversations of a person. */
  "ASSISTANT_ACT_AS_REFUSED",
  /** The chat still waits for the answer to an earlier question: one question at a time. */
  "ASSISTANT_TURN_BUSY",
  /** The proposal is not on the table any more: replaced, confirmed, stopped. */
  "ASSISTANT_ACTION_CLOSED",
  /** The suggestion is finished (done, closed or no longer true). */
  "ASSISTANT_CARD_FINISHED",
  /** The AI Assistant package cannot be added or removed in Desk now (billing off, a demo, read-only). */
  "ASSISTANT_PACKAGE_UNAVAILABLE",
  /** The voice question could not be read: the transcription service failed or is not set up. */
  "ASSISTANT_VOICE_FAILED",

  // Webhooks
  "WEBHOOK_SIGNATURE_INVALID",
  "WEBHOOK_PAYLOAD_INVALID",
] as const;

export const errorCodeSchema = z.enum(errorCodes);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

type ProblemDefinition = {
  status: number;
  title: string;
  /** Default English copy. Services override `detail` when they can say something more specific. */
  detail: string;
};

export const problemRegistry: Record<ErrorCode, ProblemDefinition> = {
  UNAUTHENTICATED: {
    status: 401,
    title: "Sign in required",
    detail: "This request has no sign-in. Sign in again.",
  },
  TOKEN_INVALID: {
    status: 401,
    title: "Session expired",
    detail: "Your sign-in is not valid any more. Sign in again.",
  },
  OTP_INVALID: {
    status: 401,
    title: "That code is not right",
    detail: "Check the code we sent you, or ask for a new one.",
  },
  OTP_TOO_MANY_ATTEMPTS: {
    status: 429,
    title: "Too many tries",
    detail: "That code is no longer usable. Ask for a new one.",
  },
  OTP_SEND_FAILED: {
    status: 502,
    title: "We could not send the code",
    detail: "Check the number or the email address, then try again.",
  },
  IDENTIFIER_IN_USE: {
    status: 409,
    title: "Already in use",
    detail: "This email is already on another Teamna account.",
  },
  LAST_SIGN_IN_METHOD: {
    status: 409,
    title: "Last way to sign in",
    detail: "Add and verify another way to sign in first.",
  },
  FORBIDDEN: {
    status: 403,
    title: "Not allowed",
    detail: "You do not have access to this business or this record.",
  },
  APP_PINNED: {
    status: 403,
    title: "Not in this app",
    detail: "This app is for one business. Open the Teamna app to use your other businesses.",
  },
  ROLE_REQUIRED: {
    status: 403,
    title: "Owner or admin only",
    detail: "Only the owner or an admin of this business can do that.",
  },
  USER_NOT_PROVISIONED: {
    status: 401,
    title: "Account not ready",
    detail: "Your account is still being set up. Try again in a moment.",
  },
  INVALID_CREDENTIALS: {
    status: 401,
    title: "Wrong username or password",
    detail: "That username and password do not match a test account.",
  },
  VALIDATION_FAILED: {
    status: 422,
    title: "Check the details",
    detail: "Some of the values you sent are not valid.",
  },
  NOT_FOUND: { status: 404, title: "Not found", detail: "That record does not exist." },
  CONFLICT: {
    status: 409,
    title: "Cannot do that now",
    detail: "Someone changed this record first. Reload the page and try again.",
  },
  RATE_LIMITED: {
    status: 429,
    title: "Too many attempts",
    detail: "Too many requests. Wait a moment and try again.",
  },
  ACT_AS_BUSINESS_REQUIRED: {
    status: 400,
    title: "Choose a business",
    detail: "Send the business to act as in the X-Act-As-Business header.",
  },
  BILLING_READ_ONLY: {
    status: 402,
    title: "Desk is read-only",
    detail: "The Teamna plan of this business is not paid. Add a card on the Billing page to make changes again.",
  },
  IDEMPOTENCY_KEY_REQUIRED: {
    status: 400,
    title: "Missing Idempotency-Key",
    detail: "This request needs an Idempotency-Key header, so that a retry is safe.",
  },
  IDEMPOTENCY_KEY_REUSED: {
    status: 409,
    title: "Key already used",
    detail: "That Idempotency-Key was already used for a different request body.",
  },
  IDEMPOTENCY_IN_PROGRESS: {
    status: 409,
    title: "Still processing",
    detail: "An identical request is still being processed. Retry in a moment.",
  },
  INTERNAL_ERROR: {
    status: 500,
    title: "Something went wrong",
    detail: "Something went wrong on our side. We logged the error. Try again in a moment.",
  },
  INVITE_NOT_FOUND: {
    status: 404,
    title: "Invite not found",
    detail: "That invite code does not exist. Ask the business for a fresh link.",
  },
  INVITE_EXPIRED: {
    status: 410,
    title: "Invite expired",
    detail: "That invite has expired. Ask the business for a fresh link.",
  },
  INVITE_EXHAUSTED: {
    status: 410,
    title: "Invite already used",
    detail: "That invite has been used the maximum number of times.",
  },
  ALREADY_A_MEMBER: {
    status: 409,
    title: "Already joined",
    detail: "You are already a member of this business.",
  },
  MEMBER_HAS_LEFT: {
    status: 409,
    title: "This member has left",
    detail: "This member left the business, so you cannot book for them. Invite them again with the join code.",
  },
  MEMBERSHIP_NOT_ACTIVE: {
    status: 403,
    title: "Membership paused",
    detail: "Your membership with this business is not active. Contact the business.",
  },
  PARTICIPANT_NOT_FOUND: {
    status: 404,
    title: "Profile not found",
    detail: "That person is not on your account.",
  },
  ACCOUNT_DELETE_BLOCKED: {
    status: 409,
    title: "Your account cannot be deleted yet",
    detail: "Something still depends on your account. Open Your data to see what to do first.",
  },
  PARTICIPANT_HAS_BOOKINGS: {
    status: 409,
    title: "Upcoming sessions booked",
    detail:
      "This profile still has upcoming sessions booked. Cancel them first, or ask the business to remove them.",
  },
  PHOTO_NOT_ALLOWED: {
    status: 409,
    title: "Photo not allowed",
    detail: "The family does not allow a profile photo of this person.",
  },
  CONVERSATION_NOT_FOUND: {
    status: 404,
    title: "Conversation not found",
    detail: "That conversation does not exist, or you are not part of it.",
  },
  ANNOUNCEMENT_READ_ONLY: {
    status: 403,
    title: "Announcements are one way",
    detail: "Only the business can post announcements. Reply in your private chat with the business instead.",
  },
  MESSAGE_RATE_LIMITED: {
    status: 429,
    title: "Slow down",
    detail: "Too many messages in a short time. Wait a few minutes and try again.",
  },
  SESSION_NOT_FOUND: {
    status: 404,
    title: "Session not found",
    detail: "That session does not exist.",
  },
  OTHER_GROUP_SESSION: {
    status: 403,
    title: "Not their group",
    detail: "This session is for another group. Book a session of their own group, or ask the business.",
  },
  SESSION_FULL: {
    status: 409,
    title: "Session full",
    detail: "That session is fully booked. Pick another time.",
  },
  SESSION_CANCELLED: {
    status: 409,
    title: "Session cancelled",
    detail: "That session has been cancelled by the business.",
  },
  ATTENDANCE_TOO_EARLY: {
    status: 409,
    title: "Too early to mark",
    detail: "You can mark who came from the day of the session. This session is on a later day.",
  },
  NOT_YOUR_SESSION: {
    status: 403,
    title: "Not your session",
    detail: "You can mark who came only on the sessions you run. Ask the owner to put you on this session.",
  },
  ATTENDANCE_CHANGED: {
    status: 409,
    title: "Attendance changed",
    detail: "Someone changed this session on another device. Check the list again, then save.",
  },
  COVER_ALREADY_ASKED: {
    status: 409,
    title: "Cover already asked",
    detail: "There is an open cover request for this session. Wait for an answer, or cancel it first.",
  },
  COVER_NOBODY_TO_ASK: {
    status: 409,
    title: "Nobody to ask",
    detail: "Nobody else is on the team, so nobody can take this session. Ask the owner.",
  },
  COVER_TAKEN: {
    status: 409,
    title: "Someone took it",
    detail: "Another person took this cover first.",
  },
  COVER_CLOSED: {
    status: 409,
    title: "Cover request closed",
    detail: "This cover request is closed: it was taken back, it expired, or the session changed.",
  },
  COVER_NOT_OFFERED: {
    status: 403,
    title: "Not offered to you",
    detail: "This cover request is not for you. Only the people who got the offer can take it.",
  },
  SESSION_STARTED: {
    status: 409,
    title: "Session already started",
    detail: "That session has already started, so it can no longer be changed.",
  },
  BOOKING_NOT_FOUND: {
    status: 404,
    title: "Booking not found",
    detail: "That booking does not exist.",
  },
  ALREADY_BOOKED: {
    status: 409,
    title: "Already booked",
    detail: "That person is already booked into this session.",
  },
  WAITLIST_CLOSED: {
    status: 409,
    title: "Waiting list closed",
    detail: "The waiting list of this session is closed. It closes some hours before the session starts.",
  },
  WAITLIST_OFFER_EXPIRED: {
    status: 409,
    title: "The offer ended",
    detail: "The time to accept this place ended. The place went to the next person on the waiting list.",
  },
  SESSION_NOT_FULL: {
    status: 409,
    title: "A place is free",
    detail: "This session has a free place. Book it instead.",
  },
  SESSION_CHANGED: {
    status: 409,
    title: "Session changed",
    detail: "The business moved this session after you picked it. Look at the new time, then pick it again.",
  },
  WAITLIST_ENTRY_NOT_FOUND: {
    status: 404,
    title: "Not on the waiting list",
    detail: "That place on the waiting list does not exist any more.",
  },
  TIMETABLE_PROBLEMS: {
    status: 409,
    title: "Not published",
    detail: "The schedule has problems to fix first. Nothing changed.",
  },
  BOOKINGS_REFUSED: {
    status: 409,
    title: "Not booked",
    detail: "Some sessions could not be booked, so nothing was booked. Change them and try again.",
  },
  BOOKING_NOT_CONFIRMED: {
    status: 409,
    title: "Booking not active",
    detail: "That booking is already cancelled or rescheduled.",
  },
  BOOK_TOO_SOON: {
    status: 409,
    title: "Too close to the start",
    detail: "The business does not accept bookings this close to the session start.",
  },
  BOOK_TOO_FAR_AHEAD: {
    status: 409,
    title: "Not open for booking yet",
    detail: "That session is further ahead than the business allows bookings.",
  },
  GROUP_MISMATCH: {
    status: 409,
    title: "Package does not cover this booking",
    detail: "The sessions on your package are for a different group. Buy a package for this group.",
  },
  POLICY_CUTOFF_PASSED: {
    status: 409,
    title: "Cancellation cut-off passed",
    detail: "The cancellation cut-off for this session has passed.",
  },
  RESCHEDULE_LIMIT_REACHED: {
    status: 409,
    title: "Reschedule limit reached",
    detail: "You have used all the reschedules the business allows.",
  },
  RESCHEDULE_NOT_ALLOWED: {
    status: 409,
    title: "Cannot reschedule",
    detail: "This booking cannot be rescheduled.",
  },
  INSUFFICIENT_CREDITS: {
    status: 409,
    title: "No sessions left",
    detail: "You have no sessions left for this booking. Buy a package to continue.",
  },
  PACKAGE_EXPIRED: {
    status: 409,
    title: "Package ends first",
    detail: "Your package ends before this session starts, so it cannot pay for it.",
  },
  PACKAGE_PAUSED: {
    status: 409,
    title: "Package paused",
    detail: "This package is paused on the day of the session, so it cannot pay for it.",
  },
  PACKAGE_NOT_FOUND: {
    status: 404,
    title: "Package not found",
    detail: "That package is not on sale.",
  },
  PAYMENT_NOT_FOUND: {
    status: 404,
    title: "Payment not found",
    detail: "That payment does not exist.",
  },
  PAYMENT_NOT_REFUNDABLE: {
    status: 409,
    title: "Payment cannot be refunded",
    detail: "Only a succeeded payment can be refunded, and not for more than its remaining amount.",
  },
  PAYMENT_REQUEST_NOT_FOUND: {
    status: 404,
    title: "Payment request not found",
    detail: "That payment request does not exist.",
  },
  PAYMENT_REQUEST_NOT_PAYABLE: {
    status: 409,
    title: "Nothing to pay",
    detail: "That payment request is already paid or has been cancelled.",
  },
  PAYMENT_PROVIDER_ERROR: {
    status: 502,
    title: "Payment provider problem",
    detail: "The payment provider rejected the request. Try again shortly.",
  },
  WHATSAPP_UNAVAILABLE: {
    status: 502,
    title: "WhatsApp did not answer",
    detail: "Teamna could not reach WhatsApp. Try again in a moment.",
  },
  PAYOUT_ACCOUNT_MISSING: {
    status: 409,
    title: "Payouts not set up",
    detail: "This business has not finished connecting a payout account, so it cannot take payments yet.",
  },
  CARD_PAYMENTS_COMING_SOON: {
    status: 409,
    title: "Card payments are coming soon",
    detail: "Card payments are not available in Teamna yet. Record cash and bank transfer payments for now.",
  },
  SIGN_UP_CLOSED: {
    status: 403,
    title: "Teamna is in early access",
    detail:
      "Teamna is in early access. To set up a new business, send the early access form on teamna.app. When we let you in, sign in with the mobile number or email from your request.",
  },
  NEW_CLIENTS_ONLY: {
    status: 409,
    title: "Only for a first purchase",
    detail: "This package is only for a person with no earlier purchase and no attended session here. Pick another package.",
  },
  MEMBERSHIP_CAP_REACHED: {
    status: 409,
    title: "No sessions left this month",
    detail: "You used every session of your membership this month. You can book again when the next month starts.",
  },
  MEMBERSHIP_MONTH_UNPAID: {
    status: 409,
    title: "This month is not paid",
    detail: "The month of this session is not paid yet. Pay it in Payments, then book the session.",
  },
  MEMBERSHIP_RUNNING: {
    status: 409,
    title: "Membership already running",
    detail: "This person has a membership here already. Cancel it first, or wait until it ends.",
  },
  MEMBERSHIP_NOT_FOUND: {
    status: 404,
    title: "No membership",
    detail: "There is no running membership here.",
  },
  MEMBERSHIP_NO_FREEZE: {
    status: 409,
    title: "A membership cannot be paused",
    detail: "A monthly membership cannot be paused. Cancel it at the end of the month instead.",
  },
  ASSISTANT_OFF: {
    status: 403,
    title: "AI Assistant is not in your plan",
    detail: "AI Assistant is a separate package. The owner or an admin can add it in Desk, under Billing.",
  },
  ASSISTANT_DAILY_LIMIT: {
    status: 429,
    title: "No more questions today",
    detail: "This business used all its AI Assistant questions for today. Try again tomorrow.",
  },
  ASSISTANT_ACT_AS_REFUSED: {
    status: 403,
    title: "Not from Control Center",
    detail: "AI Assistant conversations belong to one person. Act-as cannot open them. Use AI Assistant review in Control Center.",
  },
  ASSISTANT_TURN_BUSY: {
    status: 409,
    title: "AI Assistant is still answering",
    detail: "AI Assistant is answering your last question in this chat. Wait for the answer, then ask again.",
  },
  ASSISTANT_ACTION_CLOSED: {
    status: 409,
    title: "This plan is not on the table",
    detail: "A newer plan took its place, or the chat ended. Nothing was done.",
  },
  ASSISTANT_CARD_FINISHED: {
    status: 409,
    title: "This suggestion is finished",
    detail: "Somebody finished this suggestion, or it is no longer true.",
  },
  ASSISTANT_PACKAGE_UNAVAILABLE: {
    status: 409,
    title: "You cannot change the package here now",
    detail: "Write to Teamna in Help and support to add or remove AI Assistant.",
  },
  ASSISTANT_VOICE_FAILED: {
    status: 424,
    title: "We could not read your voice",
    detail: "Try again, or type your question.",
  },
  WEBHOOK_SIGNATURE_INVALID: {
    status: 401,
    title: "Bad signature",
    detail: "The webhook signature did not verify.",
  },
  WEBHOOK_PAYLOAD_INVALID: {
    status: 400,
    title: "Bad webhook payload",
    detail: "The webhook body did not match the expected shape.",
  },
};

export const problemSchema = z.object({
  /** A URN identifying the problem type, always `urn:teamna:error:<code>`. */
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  code: errorCodeSchema,
  /** Present when `code` is VALIDATION_FAILED: a flat map of field path to message. */
  errors: z.record(z.string(), z.array(z.string())).optional(),
  /** Echo of the `x-request-id` header, so a screenshot is enough to find the log line. */
  requestId: z.string().optional(),
  /** Free-form extras a specific error attaches, for example `spotsLeft` on SESSION_FULL. */
  meta: z.record(z.string(), z.unknown()).optional(),
});
export type Problem = z.infer<typeof problemSchema>;

export function problemTypeUrn(code: ErrorCode): string {
  return `urn:teamna:error:${code}`;
}

export function statusForCode(code: ErrorCode): number {
  return problemRegistry[code].status;
}

export function buildProblem(
  code: ErrorCode,
  overrides: {
    detail?: string;
    requestId?: string;
    errors?: Record<string, string[]>;
    meta?: Record<string, unknown>;
  } = {},
): Problem {
  const definition = problemRegistry[code];
  return {
    type: problemTypeUrn(code),
    title: definition.title,
    status: definition.status,
    detail: overrides.detail ?? definition.detail,
    code,
    ...(overrides.errors ? { errors: overrides.errors } : {}),
    ...(overrides.requestId ? { requestId: overrides.requestId } : {}),
    ...(overrides.meta ? { meta: overrides.meta } : {}),
  };
}
